// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React from 'react';
import { Card, Tag, Typography } from '@kintent/glide';
import { useCurrentRoute } from 'react-navi';

import * as Styles from './EnterpriseCertificationDetail.styles';
import ComplianceStandardLogoContainer from '../components/ComplianceStandardLogoContainer';
import {
  CERTIFICATION_STATUS_DISPLAY_NAME_MAP,
  getCertificationCardTitle,
  getCertificationLogo,
  getCertificationLogoSrcSet,
} from '../../../../lib/constants';
import { Flex } from '../../../../components/Flex';
import { useAuthService, useContentVisibility, useProgramContent } from '../../../../lib/state';
import getDescription from '../lib/getDescription';
import { CertificationDate } from '../components/CertificationDate';
import { CertificationAuditFirm } from '../components/CertificationAuditFirm';
import CertificationReadiness from '../components/CertificationReadiness';

function Metadata({ selectedCertification, selectedDocument }) {
  const [programContent] = useProgramContent();
  const { currentTeam } = useAuthService();
  const { certification } = selectedCertification;

  const description = getDescription({
    selectedCertification,
    currentTeam,
    selectedDocument,
    programContent,
  });

  return (
    <Flex
      direction="column"
      gap="16px"
    >
      <Flex
        alignItems="center"
        gap="16px"
      >
        <ComplianceStandardLogoContainer>
          <img
            src={getCertificationLogo(certification.shortName, selectedCertification.subtype)}
            srcSet={getCertificationLogoSrcSet(certification.shortName, selectedCertification.subtype)}
            alt="Certification Logo"
          />
        </ComplianceStandardLogoContainer>
        <Typography
          as="h3"
          level="3"
        >
          {getCertificationCardTitle(certification.shortName, selectedCertification.subtype)}
        </Typography>
      </Flex>
      {selectedDocument && <Typography level="4">{selectedDocument.title}</Typography>}
      <Typography as="p">{description}</Typography>
    </Flex>
  );
}

function CertificationAchievementMetadata({ selectedCertification, selectedDocument }) {
  const artifact = selectedDocument ?? selectedCertification;

  return (
    <Styles.AchievementContainer>
      {artifact?.status && (
        <div>
          <Typography level="9">Status</Typography>
          <Tag
            type="system"
            variant="positive"
          >
            {CERTIFICATION_STATUS_DISPLAY_NAME_MAP[artifact.status]}
          </Tag>
        </div>
      )}
      <CertificationDate
        selectedCertification={selectedCertification}
        selectedDocument={selectedDocument}
      />
      <CertificationAuditFirm
        selectedCertification={selectedCertification}
        selectedDocument={selectedDocument}
      />
    </Styles.AchievementContainer>
  );
}

function EnterpriseCertificationDetail({ selectedCertification, selectedDocument = null }) {
  const { certificationAccessLevelMap } = useContentVisibility();
  const {
    data: { slug },
  } = useCurrentRoute();

  const shouldHideCertificationDetail = certificationAccessLevelMap[slug];

  return (
    <div
      className="certification-detail-content"
      data-testid="enterprise-certification-detail-panel"
    >
      <Card className="certification-metadata-content">
        <Flex
          direction="column"
          gap="16px"
        >
          <Metadata
            selectedCertification={selectedCertification}
            selectedDocument={selectedDocument}
          />
          <CertificationAchievementMetadata
            selectedCertification={selectedCertification}
            selectedDocument={selectedDocument}
          />
        </Flex>
      </Card>
      {!shouldHideCertificationDetail && (
        <CertificationReadiness
          asCard
          selectedCertification={selectedCertification}
        />
      )}
    </div>
  );
}

export default EnterpriseCertificationDetail;
